import React from 'react';
import { formatSize } from 'pano-utils';
import { type CFSeqFrame, CFViewerPlayType, ImageType, LibType } from '@types';
import { Collapse } from '@components/Collapse';
import ImageContainer from '@components/ImageContainer';
import Image from '@components/Image';
import Button from '@components/Button';
import Textarea from '@components/Textarea';
import useTextareaHandelrs from '@hooks/useTextareaHandelrs';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import Slider from '@components/Slider';
import { Validator } from '@components/Validator';
import { useCustomFeature } from '../CustomFeatureContext';
import { LIB_SIZE_LIMIT, selectLibSeqFrames } from '../../mediaLib';
import CMoreLinkSetting from './CMoreLinkSetting';

const sizeLimit = formatSize(LIB_SIZE_LIMIT[LibType.sequenceFrame]);

const items: DropdownItem<CFViewerPlayType>[] = [
  { id: CFViewerPlayType.cick, name: '不自动播放' },
  { id: CFViewerPlayType.auto, name: '自动播放环物（只播放1次）' },
  { id: CFViewerPlayType.loop, name: '自动播放环物（循环播放）' },
];

function CSeqFrameSetting() {
  const { data, setData } = useCustomFeature<CFSeqFrame>();
  const { imgs, content, playMethod, playSpeed } = data;

  const textHandlers = useTextareaHandelrs('trimRight', (val) =>
    setData((draft) => {
      draft.content = val;
    })
  );

  const selectImages = async () => {
    const { libItems } = await selectLibSeqFrames();
    if (!libItems) return;
    const item = libItems[0];
    setData((draft) => {
      draft.imgs = {
        mediaId: item.id,
        mediaPath: item.url,
        mediaFrame: item.frame,
        mediaSuffix: item.suffix,
      };
    });
  };

  return (
    <>
      <Collapse title="逐帧图片设置">
        <Validator
          value={imgs}
          validFn={(value) => !!value.mediaPath}
          warningMessage="请选择环物图片"
        >
          {({ value, warning }) => (
            <ImageContainer
              warning={warning}
              placeholder={
                <div className="px-20 text-center">请选择环物图片</div>
              }
              className="h-115"
              onDeleteClick={() =>
                setData((draft) => {
                  draft.imgs = {};
                  draft.content = '';
                })
              }
            >
              {!!value.mediaPath && (
                <>
                  <Image
                    className="max-h-full max-w-full"
                    src={`${value.mediaPath}/1.${value.mediaSuffix}`}
                    type={ImageType.media}
                  />
                  <div className="absolute right-2 bottom-2  bg-black-black py-2 px-5 text-sm opacity-50">
                    {`${value.mediaFrame}帧`}
                  </div>
                </>
              )}
            </ImageContainer>
          )}
        </Validator>
        <div className="mt-10 flex items-center justify-between">
          <div>{`单图片不超过${sizeLimit}`}</div>
          <Button size="sm" onClick={selectImages}>
            选择图片
          </Button>
        </div>
        <div className="mt-20 mb-10">
          <Textarea
            className="h-115 w-full"
            scheme="dark"
            placeholder="为图片添加文字描述"
            maxLength={1000}
            value={content}
            {...textHandlers}
          />
        </div>
      </Collapse>
      <Collapse title="播放设置">
        <Dropdown
          items={items}
          selected={playMethod}
          onChange={(id) =>
            setData((draft) => {
              draft.playMethod = id;
            })
          }
        />
        {playMethod !== CFViewerPlayType.cick && (
          <Slider
            className="mt-20"
            min={1}
            max={60}
            valToString={(val) => `${val}帧/秒`}
            value={playSpeed}
            onChange={(val) =>
              setData((draft) => {
                draft.playSpeed = val;
              })
            }
          >
            <Slider.Header title="播放速度" />
            <Slider.Bar />
            <Slider.Footer />
          </Slider>
        )}
      </Collapse>
      <CMoreLinkSetting />
    </>
  );
}

export default CSeqFrameSetting;
